import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Ghost, Sparkles } from "lucide-react";
import { KineticLine } from "./Reveal";

export const Hero = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 55, damping: 16 });
  const sy = useSpring(my, { stiffness: 55, damping: 16 });
  const px = useTransform(sx, (v) => v * 26);
  const py = useTransform(sy, (v) => v * 18);
  const rx = useTransform(sy, (v) => v * -8);
  const ry = useTransform(sx, (v) => v * 10);

  const onMove = (e) => {
    mx.set(e.clientX / window.innerWidth - 0.5);
    my.set(e.clientY / window.innerHeight - 0.5);
  };

  return (
    <section
      id="top"
      onMouseMove={onMove}
      className="relative min-h-screen flex flex-col items-center justify-center text-center px-4 pt-28 pb-20 overflow-hidden"
      data-testid="hero-section"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.15 }}
        className="mono text-[10px] sm:text-xs tracking-[0.35em] text-[#B6FF00] border border-[#B6FF00]/40 rounded-full px-5 py-2 mb-8 flex items-center gap-2 bg-[#19002E]/60"
        data-testid="hero-badge"
      >
        <Sparkles className="w-3.5 h-3.5" />
        SOLANA MEMECOIN · SPOOKY SEASON LIVE
        <Ghost className="w-3.5 h-3.5" />
      </motion.div>

      <h1
        className="glitch font-glitch text-6xl sm:text-8xl lg:text-9xl leading-none text-[#FF6B00] glow-orange select-none"
        data-text="$HALLOWIN"
        data-testid="hero-title"
      >
        <motion.span
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="block"
        >
          $HALLOWIN
        </motion.span>
      </h1>

      <div className="mt-4 font-display font-bold uppercase tracking-tight text-2xl sm:text-4xl lg:text-5xl" data-testid="hero-tagline">
        <KineticLine delay={0.55}>
          <span className="text-white">THIS HALLOWEEN,</span>
        </KineticLine>
        <KineticLine delay={0.72}>
          <span className="text-[#B6FF00] glow-green">WE ALL WIN.</span>
        </KineticLine>
      </div>

      <motion.div
        style={{ x: px, y: py, rotateX: rx, rotateY: ry, transformPerspective: 900 }}
        className="relative my-8 sm:my-10"
        data-testid="hero-mascot"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.6, rotate: -8 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 1.1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="absolute inset-0 -m-10 rounded-full bg-[#FF6B00]/20 blur-3xl animate-flicker" />
          <img
            src="/mascot.jpg"
            alt="HALLOWIN mischievous pumpkin mascot with glowing eyes"
            className="relative w-56 sm:w-72 lg:w-96 rounded-full animate-pumpkin drop-glow-green ring-4 ring-[#FF6B00]/40"
          />
        </motion.div>
        <Ghost className="absolute -left-8 -top-4 w-8 h-8 text-[#B6FF00]/60 animate-float" />
        <Ghost className="absolute -right-10 top-1/3 w-10 h-10 text-[#A020F0]/60 animate-float" style={{ animationDelay: "1.2s" }} />
      </motion.div>

      <motion.p
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.95 }}
        className="mono text-sm sm:text-lg text-purple-200/80 max-w-xl"
        data-testid="hero-subtitle"
      >
        No tricks. Just memes. Welcome to the spookiest season on Solana.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.1 }}
        className="mt-10 flex flex-col sm:flex-row items-center gap-4 sm:gap-6"
      >
        <a
          href="#buy"
          id="buy"
          data-testid="buy-hallowin-button"
          className="bg-[#B6FF00] text-black font-display font-bold uppercase px-9 py-4 rounded-full text-base sm:text-lg tracking-wider shadow-[0_0_25px_rgba(182,255,0,0.6)] hover:bg-[#c8ff3d] hover:scale-105 hover:shadow-[0_0_45px_rgba(182,255,0,0.9)] active:scale-95 transition-all duration-200 flex items-center gap-2"
        >
          🎃 BUY $HALLOWIN
        </a>
        <a
          href="https://x.com/i/communities/1972316257106997599"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="join-cult-button"
          className="bg-transparent text-[#FF6B00] border-2 border-[#FF6B00] font-display font-bold uppercase px-9 py-4 rounded-full text-base sm:text-lg tracking-wider shadow-[0_0_25px_rgba(255,107,0,0.35)] hover:bg-[#FF6B00] hover:text-black hover:scale-105 hover:shadow-[0_0_45px_rgba(255,107,0,0.8)] active:scale-95 transition-all duration-200 flex items-center gap-2"
        >
          👻 JOIN THE CULT
        </a>
      </motion.div>
    </section>
  );
};
